import { useState, useEffect, useRef } from 'react'
import { Box, Typography } from '@mui/material'
import logo from '../../assets/rounded_logo.svg'

export default function ToursListFrame() {
  const [isVisible, setIsVisible] = useState(false)
  const [hasAnimated, setHasAnimated] = useState(false)
  const frameRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true)
          setHasAnimated(true)
        }
      },
      {
        threshold: 0.33,
        rootMargin: '0px 0px -50px 0px'
      }
    )

    if (frameRef.current) {
      observer.observe(frameRef.current)
    }

    return () => {
      if (frameRef.current) {
        observer.unobserve(frameRef.current)
      }
    }
  }, [hasAnimated])

  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  const dateLabel = tomorrow.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  })

  const tours = [
    { initial: 'L', name: 'Lonnie M.', time: '10:00 AM', avatarColor: '#fbbf24' },
    { initial: 'D', name: 'Dexter T.', time: '1:30 PM', avatarColor: '#3b82f6' },
    { initial: 'M', name: 'Maya R.', time: '3:00 PM', avatarColor: '#8b5cf6' },
  ]

  return (
    <Box
      ref={frameRef}
      sx={{
        position: 'relative',
        width: 280,
        height: 560,
        bgcolor: '#000',
        borderRadius: 6,
        p: 1,
        boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
      }}
    >
      {/* iPhone Frame */}
      <Box sx={{
        width: '100%',
        height: '100%',
        bgcolor: 'white',
        borderRadius: 5,
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Status Bar */}
        <Box sx={{
          height: 44,
          bgcolor: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          px: 2,
          fontSize: '0.875rem',
          fontWeight: 600
        }}>
          <Box
            component="img"
            src={logo}
            alt="HeyProperty"
            sx={{
              height: 40,
              width: 'auto',
              maxWidth: '60px',
              marginTop: '5px'
            }}
          />
        </Box>

        {/* Animated Banner */}
        <Box sx={{
          bgcolor: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '8px',
          p: 2,
          mx: 2,
          mb: 2,
          mt: 1,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          transform: isVisible ? 'translateY(0)' : 'translateY(-100px)',
          opacity: isVisible ? 1 : 0,
          transition: 'transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.4s ease-out',
          position: 'relative',
          zIndex: 10
        }}>
          <Typography sx={{
            fontSize: '0.875rem',
            color: '#92400e',
            fontWeight: 500,
            lineHeight: 1.4,
          }}>
            🔔 3 tours booked while you were away
          </Typography>
        </Box>

        {/* Tours List */}
        <Box sx={{ px: 2 }}>
          <Typography sx={{
            fontWeight: 700,
            fontSize: '0.875rem',
            color: 'grey.900',
            mb: 1.5
          }}>
            {dateLabel}
          </Typography>

          {tours.map((tour) => (
            <Box
              key={tour.name}
              sx={{
                bgcolor: 'white',
                borderRadius: 2,
                p: 1.5,
                mb: 1.5,
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
              }}
            >
              {/* Avatar */}
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: tour.avatarColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem' }}>
                  {tour.initial}
                </Typography>
              </Box>

              {/* Details */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  color: 'grey.900',
                  mb: 0.5
                }}>
                  {tour.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                    {tour.time}
                  </Typography>
                  <Box sx={{
                    bgcolor: '#10b981',
                    borderRadius: '4px',
                    px: 0.75,
                    py: 0.25,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    <Typography sx={{ color: 'white', fontSize: '0.688rem', fontWeight: 700 }}>
                      ✓ Screened
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Bottom Navigation */}
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 60,
          bgcolor: '#f9fafb',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          px: 2
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ fontSize: '1.25rem', color: 'grey.400', mb: 0.5 }}>🏠</Box>
            <Typography sx={{ fontSize: '0.625rem', color: 'grey.400' }}>Home</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ fontSize: '1.25rem', color: 'grey.900', mb: 0.5 }}>📅</Box>
            <Typography sx={{ fontSize: '0.625rem', color: 'grey.900', fontWeight: 700 }}>Tours</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ fontSize: '1.25rem', color: 'grey.400', mb: 0.5 }}>💬</Box>
            <Typography sx={{ fontSize: '0.625rem', color: 'grey.400' }}>Prospects</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
